import { MapPin, Phone, Mail, Send, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import ciedaLogo from "@/assets/cieda-logo-dark.webp";
import { CIEDA_PHONE, CIEDA_PHONE_TEL } from "@/lib/constants";

const footerLinks = [
  { label: "Produits", href: "/#brands" },
  { label: "A Propos", href: "/#about" },
  { label: "Distribution", href: "/#logistics" },
  { label: "Catalogue", href: "/#catalog" },
];

const legalLinks = [
  { label: "Politique de confidentialite", href: "/politique-confidentialite" },
  { label: "Conditions Generales de Vente", href: "/conditions-generales-vente" },
  { label: "Mentions Legales", href: "/mentions-legales" },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setSubmitting(true);
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (res.ok) {
        setSubscribed(true);
        setEmail("");
        setTimeout(() => setSubscribed(false), 4000);
      }
    } catch {
      // Silently fail — non-critical feature
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer id="contact" className="bg-primary text-primary-foreground relative overflow-hidden">
      <div className="h-1 w-full" style={{ background: "var(--gold-gradient)" }} />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-[120px]" />

      <div className="container-max px-5 sm:px-8 lg:px-12 py-16 md:py-20 relative">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-14">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img src={ciedaLogo} alt="CIEDA" className="h-36 w-auto mb-5" loading="lazy" decoding="async" />
            <p className="text-primary-foreground/50 text-sm leading-relaxed max-w-sm mb-6">
              Compagnie Import Export & Distribution Africaine — Votre partenaire de confiance pour la distribution de produits de qualite en Afrique de l'Ouest.
            </p>

            {/* Newsletter */}
            <form onSubmit={handleSubscribe} className="max-w-sm">
              <label htmlFor="newsletter-email" className="block text-xs text-primary-foreground/40 font-medium mb-2">
                Newsletter
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
              <input
                id="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre email professionnel"
                aria-label="Votre email professionnel"
                className="flex-1 px-4 py-2.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/10 text-primary-foreground text-sm placeholder:text-primary-foreground/30 focus:outline-none focus:border-secondary/40 transition-colors"
                required
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2.5 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-gold-light transition-colors flex items-center gap-1.5 disabled:opacity-60"
              >
                <Send size={13} />
                {subscribed ? "Merci !" : submitting ? "..." : "S'abonner"}
              </button>
              </div>
            </form>
            <p className="text-xs text-primary-foreground/25 mt-2">Recevez nos offres et nouveautes produits</p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-semibold text-secondary text-xs uppercase tracking-[0.15em] mb-5">Navigation</h4>
            <nav className="space-y-3">
              {footerLinks.map((link) => (
                <a key={link.href} href={link.href} className="block text-primary-foreground/50 hover:text-primary-foreground transition-colors text-sm">
                  {link.label}
                </a>
              ))}
              {/* External — Djayma boutique */}
              <a
                href="https://djayma.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-secondary/80 hover:text-secondary transition-colors text-sm font-medium"
              >
                <ExternalLink size={13} />
                Commander sur Djayma
              </a>
            </nav>

            {/* Legal links */}
            <h4 className="font-semibold text-secondary text-xs uppercase tracking-[0.15em] mb-4 mt-8">Informations legales</h4>
            <nav className="space-y-3">
              {legalLinks.map((link) => (
                <Link key={link.href} to={link.href} className="block text-primary-foreground/50 hover:text-primary-foreground transition-colors text-sm">
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-secondary text-xs uppercase tracking-[0.15em] mb-5">Contact</h4>
            <div className="space-y-4 text-sm text-primary-foreground/50">
              <div className="flex items-start gap-3">
                <MapPin size={15} className="mt-0.5 flex-shrink-0 text-secondary/60" />
                <span>Hann Mariste II<br />Dakar, Senegal</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={15} className="flex-shrink-0 text-secondary/60" />
                <a href={CIEDA_PHONE_TEL} className="hover:text-primary-foreground transition-colors">{CIEDA_PHONE}</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={15} className="flex-shrink-0 text-secondary/60" />
                <a href="mailto:contact@ciedagroup.com" className="hover:text-primary-foreground transition-colors">contact@ciedagroup.com</a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-primary-foreground/8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-primary-foreground/30">&copy; {new Date().getFullYear()} CIEDA — Tous droits reserves.</p>
          <div className="flex items-center gap-4">
            <Link to="/politique-confidentialite" className="text-xs text-primary-foreground/30 hover:text-primary-foreground/50 transition-colors">
              Politique de confidentialite
            </Link>
            <Link to="/mentions-legales" className="text-xs text-primary-foreground/30 hover:text-primary-foreground/50 transition-colors">
              Mentions legales
            </Link>
            <p className="text-xs text-primary-foreground/20">Dakar, Senegal</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
